import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import betClient from '../../api/betClient';
import { useEffectiveTier } from '../../hooks/useEffectiveTier';
import { useAuth } from '../../contexts/AuthContext';
import LockedContentOverlay from '../upgrade/LockedContentOverlay';

function renderMarkdown(text) {
  const parts = text.split(/(\*\*[^*]+\*\*|\*[^*]+\*)/g);
  return parts.map((part, j) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={j} className="font-semibold text-[#111827] dark:text-[#e2e8f0]">{part.slice(2, -2)}</strong>;
    }
    if (part.startsWith('*') && part.endsWith('*') && part.length > 2) {
      return <em key={j}>{part.slice(1, -1)}</em>;
    }
    return part;
  });
}

const SPORTS = [
  { key: 'americanfootball_nfl', label: 'NFL' },
  { key: 'americanfootball_ncaaf', label: 'NCAAF' },
  { key: 'basketball_nba', label: 'NBA' },
  { key: 'basketball_ncaab', label: 'NCAAB' },
  { key: 'baseball_mlb', label: 'MLB' },
  { key: 'icehockey_nhl', label: 'NHL' },
  { key: 'soccer_usa_mls', label: 'MLS' },
  { key: 'mma_mixed_martial_arts', label: 'MMA' },
  { key: 'boxing_boxing', label: 'Boxing' },
];

function fmtOdds(n) { return n == null ? '—' : n > 0 ? `+${n}` : `${n}`; }
function fmtPct(n) { return n == null ? '—' : `${n.toFixed(1)}%`; }

export default function PredictionsPage() {
  const [sport, setSport] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [detailLoading, setDetailLoading] = useState(null);
  const [details, setDetails] = useState({});
  const tier = useEffectiveTier();
  const { loading: authLoading } = useAuth();

  function fetchPredictions(force = false, signal = null) {
    if (!sport) return;
    setLoading(true); setError(null); setData(null);
    const config = signal ? { signal } : {};
    betClient.get(`/api/ai/predictions/${sport}${force ? '?force=true' : ''}`, config)
      .then((r) => setData(r.data))
      .catch((e) => { if (!signal?.aborted) setError(e.message); })
      .finally(() => { if (!signal?.aborted) setLoading(false); });
  }

  useEffect(() => {
    if (!sport || authLoading) return;
    const controller = new AbortController();
    fetchPredictions(false, controller.signal);
    return () => controller.abort();
  }, [sport, tier, authLoading]);

  function toggleDetail(eventId) {
    if (expandedId === eventId) { setExpandedId(null); return; }
    setExpandedId(eventId);
    if (details[eventId]) return;
    setDetailLoading(eventId);
    betClient.get(`/api/ai/prediction/${sport}/${eventId}`)
      .then((r) => setDetails((d) => ({ ...d, [eventId]: r.data })))
      .catch(() => setDetails((d) => ({ ...d, [eventId]: { content: 'Failed to load detail.', error: true } })))
      .finally(() => setDetailLoading(null));
  }

  const predictions = data?.predictions || [];
  const valueBets = predictions.filter((p) => p.value_side);

  return (
    <div className="px-5 py-6">
      {/* Header */}
      <div className="mb-1">
        <p className="text-[12px] uppercase tracking-[1px] font-medium text-[#005BB3] dark:text-[#3b82f6]" style={{ fontFamily: 'var(--font-sans)' }}>AI Predictions</p>
      </div>
      <h1 className="text-[22px] font-semibold text-[#111827] dark:text-[#e2e8f0] tracking-tight mb-1" style={{ fontFamily: 'var(--font-sans)' }}>Game Predictions</h1>
      <p className="text-[14px] text-[#6B7280] dark:text-[#8892a8] mb-5" style={{ fontFamily: 'var(--font-sans)' }}>AI-estimated win probabilities compared to bookmaker odds. Value bets highlighted.</p>

      {/* Sport selector */}
      <div className="flex flex-wrap items-end gap-0 mb-5 border-b border-[#E5E7EB] dark:border-[#1e2536]">
        {SPORTS.map((s) => (
          <button key={s.key} onClick={() => setSport(s.key)}
            className={`px-0 mr-6 pb-2 text-[14px] whitespace-nowrap transition-colors duration-150 border-b-2 ${
              sport === s.key
                ? 'font-semibold text-[#111827] dark:text-[#e2e8f0] border-[#005BB3] dark:border-[#3b82f6]'
                : 'font-medium text-[#6B7280] dark:text-[#8892a8] border-transparent hover:text-[#4B5563] dark:hover:text-[#e2e8f0]'
            }`}
            style={{ fontFamily: 'var(--font-sans)' }}>
            {s.label}
          </button>
        ))}
        {data && !loading && (
          <button onClick={() => fetchPredictions(true)} className="ml-auto mb-2 text-[12px] text-[#005BB3] dark:text-[#3b82f6] hover:text-[#004494] transition-colors duration-150" style={{ fontFamily: 'var(--font-sans)' }}>Refresh</button>
        )}
      </div>

      {!sport && !loading && !data && (
        <div className="py-16 text-center">
          <p className="text-[16px] text-[#6B7280] dark:text-[#8892a8]" style={{ fontFamily: 'var(--font-sans)' }}>Select a sport above to generate AI predictions</p>
        </div>
      )}

      {error && (
        <div className="rounded bg-[rgba(239,68,68,0.06)] dark:bg-[rgba(239,68,68,0.08)] border border-[#fecaca] dark:border-[#7f1d1d] px-3 py-2 mb-4">
          <p className="text-[#ef4444] text-[11px]">Failed to load: {error}</p>
          <button onClick={() => fetchPredictions()} className="text-[10px] text-[#ef4444] underline mt-1">Retry</button>
        </div>
      )}

      {loading && (
        <div>
          <div className="flex items-center gap-2 mb-4">
            <svg className="animate-spin h-3.5 w-3.5 text-[#3b82f6]" viewBox="0 0 24 24" fill="none">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            <span className="text-[#8892a8] text-[11px]">Generating predictions...</span>
          </div>
          <div className="space-y-2">{[...Array(4)].map((_, i) => <div key={i} className="h-24 rounded bg-[#f1f5f9] dark:bg-[#151a25] skeleton" />)}</div>
        </div>
      )}

      {data && !loading && (
        <div className="max-w-[1100px]">
          {/* Value bets summary */}
          {valueBets.length > 0 && (
            <div className="rounded border-l-[3px] border-l-[#22c55e] border border-[#e2e8f0] dark:border-[#1e2536] bg-[rgba(34,197,94,0.04)] dark:bg-[rgba(34,197,94,0.06)] px-4 py-3 mb-4">
              <p className="text-[12px] font-semibold text-[#22c55e] mb-1">{valueBets.length} Value Bet{valueBets.length !== 1 ? 's' : ''} Found</p>
              <p className="text-[11px] text-[#8892a8]">Games where AI probability differs from bookmaker odds by 5%+</p>
            </div>
          )}

          {data.cached && (
            <p className="text-[10px] text-[#8892a8] mb-2" style={{ fontFamily: 'var(--font-mono)' }}>
              Cached predictions from {new Date(data.created_at).toLocaleString()}
            </p>
          )}

          {predictions.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-[#8892a8] text-sm">No upcoming games for this sport.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {predictions.map((pred) => (
                <PredictionCard
                  key={pred.event_id}
                  pred={pred}
                  expanded={expandedId === pred.event_id}
                  onToggle={() => toggleDetail(pred.event_id)}
                  detail={details[pred.event_id]}
                  detailLoading={detailLoading === pred.event_id}
                  sport={sport}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}


function PredictionCard({ pred, expanded, onToggle, detail, detailLoading, sport }) {
  const isValue = !!pred.value_side;
  const time = pred.commence_time ? new Date(pred.commence_time).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '';

  const homeEdgeColor = (pred.home_edge ?? 0) >= 5 ? 'text-[#22c55e]' : (pred.home_edge ?? 0) <= -5 ? 'text-[#ef4444]' : 'text-[#8892a8]';
  const awayEdgeColor = (pred.away_edge ?? 0) >= 5 ? 'text-[#22c55e]' : (pred.away_edge ?? 0) <= -5 ? 'text-[#ef4444]' : 'text-[#8892a8]';

  const confidenceColor = pred.confidence === 'high' ? 'bg-[rgba(34,197,94,0.1)] text-[#22c55e]' : pred.confidence === 'moderate' ? 'bg-[rgba(245,158,11,0.1)] text-[#f59e0b]' : 'bg-[rgba(148,163,184,0.1)] text-[#94a3b8]';

  return (
    <div className={`rounded border ${isValue ? 'border-[#22c55e]/30 dark:border-[#22c55e]/20' : 'border-[#e2e8f0] dark:border-[#1e2536]'} bg-white dark:bg-[#151a25] overflow-hidden`}>
      <button onClick={onToggle} className="w-full text-left px-4 py-3 hover:bg-[#f8fafc] dark:hover:bg-[#1a2030] transition-colors duration-150">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            {/* Teams + time */}
            <div className="flex items-center gap-2 mb-2">
              <span className="text-[12px] font-medium text-[#1e293b] dark:text-[#e2e8f0]">{pred.away_team} @ {pred.home_team}</span>
              <span className="text-[10px] text-[#8892a8]" style={{ fontFamily: 'var(--font-mono)' }}>{time}</span>
              {isValue && <span className="text-[9px] font-bold px-1.5 py-px rounded bg-[rgba(34,197,94,0.1)] text-[#22c55e]">VALUE</span>}
              <span className={`text-[9px] font-semibold px-1.5 py-px rounded ${confidenceColor}`}>{pred.confidence?.toUpperCase()}</span>
            </div>

            {/* Probability bars */}
            <div className="grid grid-cols-2 gap-3">
              <TeamProb label={pred.home_team} aiPct={pred.home_win_pct} bookPct={pred.home_implied_pct} edge={pred.home_edge} odds={pred.home_odds} isValue={pred.value_side === 'home'} />
              <TeamProb label={pred.away_team} aiPct={pred.away_win_pct} bookPct={pred.away_implied_pct} edge={pred.away_edge} odds={pred.away_odds} isValue={pred.value_side === 'away'} />
            </div>

            {/* Reasoning / Teaser */}
            {pred.reasoning ? (
              <p className="text-[11px] text-[#64748b] dark:text-[#8892a8] mt-2">{pred.reasoning}</p>
            ) : pred.teaser_hook ? (
              <p className="text-[11px] text-[#64748b] dark:text-[#8892a8] mt-2 italic">{pred.teaser_hook}</p>
            ) : null}
          </div>

          <svg className={`w-3.5 h-3.5 text-[#8892a8] flex-shrink-0 mt-1 transition-transform duration-150 ${expanded ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>

      {/* Expanded detail */}
      {expanded && (
        <div className="px-4 py-3 border-t border-[#e2e8f0] dark:border-[#1e2536]">
          {detailLoading && (
            <div className="flex items-center gap-2 py-2">
              <svg className="animate-spin h-3 w-3 text-[#3b82f6]" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              <span className="text-[#8892a8] text-[11px]">Loading detailed analysis...</span>
            </div>
          )}
          {detail && !detailLoading && (
            <div>
              {detail.content ? (
                <>
                  {detail.content.split('\n\n').map((para, i) => (
                    <p key={i} className="text-[14px] leading-[1.7] text-[#1F2937] dark:text-[#8892a8] mb-2" style={{ fontFamily: 'var(--font-sans)' }}>{renderMarkdown(para)}</p>
                  ))}
                  {pred.best_bookmaker && (
                    <div className="mt-2 pt-2 border-t border-[#e2e8f0] dark:border-[#1e2536]">
                      <p className="text-[10px] text-[#8892a8]">Best odds: <span className="text-[#22c55e] font-semibold">{pred.best_bookmaker} {fmtOdds(pred.best_odds)}</span></p>
                    </div>
                  )}
                </>
              ) : (
                <>
                  {detail.teaser_hook && <p className="text-[13px] text-[#64748b] dark:text-[#8892a8] mb-3 italic">{detail.teaser_hook}</p>}
                  <LockedContentOverlay
                    description="See the full reasoning behind this pick, including the key factors driving the model's edge."
                    triggerFeature="predictions"
                  />
                </>
              )}
              <div className="mt-2">
                <Link to={`/game/${pred.event_id}?sport=${sport}`} className="text-[10px] text-[#3b82f6] hover:text-[#2563eb] transition-colors duration-150">
                  View full game detail &rarr;
                </Link>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}


function TeamProb({ label, aiPct, bookPct, edge, odds, isValue }) {
  const edgeColor = (edge ?? 0) >= 5 ? 'text-[#22c55e]' : (edge ?? 0) <= -5 ? 'text-[#ef4444]' : 'text-[#8892a8]';
  const barColor = isValue ? 'bg-[#22c55e]' : 'bg-[#3b82f6]';

  return (
    <div>
      <div className="flex items-center justify-between mb-0.5">
        <span className={`text-[11px] font-medium ${isValue ? 'text-[#22c55e]' : 'text-[#1e293b] dark:text-[#e2e8f0]'}`}>{label}</span>
        <span className="text-[10px] text-[#8892a8]" style={{ fontFamily: 'var(--font-mono)' }}>{fmtOdds(odds)}</span>
      </div>
      {/* Bar */}
      <div className="h-1.5 rounded-full bg-[#f1f5f9] dark:bg-[#0f1219] mb-1">
        <div className={`h-full rounded-full ${barColor}`} style={{ width: `${Math.min(aiPct ?? 0, 100)}%` }} />
      </div>
      <div className="flex items-center justify-between">
        <span className="text-[10px] text-[#8892a8]" style={{ fontFamily: 'var(--font-mono)' }}>
          AI {fmtPct(aiPct)} <span className="text-[#505a6e]">/ Book {fmtPct(bookPct)}</span>
        </span>
        <span className={`text-[10px] font-semibold ${edgeColor}`} style={{ fontFamily: 'var(--font-mono)' }}>
          {edge != null ? `${edge > 0 ? '+' : ''}${edge.toFixed(1)}%` : '—'}
        </span>
      </div>
    </div>
  );
}
